import { AbstractControl } from '@angular/forms';
import {
  DUAL_CURRENCY_DEPOSIT,
  RISK_REVERSAL,
  RISK_REVERSAL_STRIP,
  STRADDLE,
  STRANGLE,
  VANILLA,
  VANILLA_STRIP
} from '@approot/constants';
import {
  FieldsToBeEdited,
  GenericLeg,
  KeyValue,
  LegAttribute,
  MidBidAsk,
  MultiHedge,
  Payload,
  Position,
  PositionAction,
  PremiumStyle,
  Price,
  Pricing,
  ProductName,
  ProductSpecification,
  QuotePair,
  QuoteSide,
  QuotedPremiumSection,
  RfqProductApiName,
  RfqQuote,
  RfqSetup,
  RfqTabLegs,
  RfqTabPosition,
  Vol
} from '@approot/models';
import { Utilities, clone, crossToPair, dvSchemas } from '@approot/utilities';
import { components } from '@approot/utilities/digital-vega-schema';
import { LegComponent } from '@approot/widgets';
import {
  getAllAttributeOptions,
  getAttributeControlFromKey
} from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import {
  optionsChangeHandler,
  updateValidatorFromMarginStyle
} from '@approot/widgets/strategy-detail/leg/utilities/leg.utilities';
import dayjs from 'dayjs';
import {
  BidMidAsk,
  ClientEventQuoteExtended,
  IndicativeQuote,
  MidPriceInfo,
  NewVolsHedgeType,
  RfqBuilderLeg,
  RfqPosition,
  VolBasedRfqOptionPriceV2Result
} from '../rfq.types';
import { ClientEventQuoteExtendedV2, ClientQuoteEventV2 } from './../rfq.types';
import { RFQDetailsDTO } from './rfq-tab.service';

const { formattedNumberToNumber } = Utilities;

type Notional = 'notional' | 'notional1' | 'notional2';

export type Product = {
  forwardPointsBid: string;
  forwardPointsAsk: string;
  volAsk: number;
  volBid: number;
};

type EventScheduleEntry = {
  notional: number;
  strike: number;
};

type OptionLegDTO = dvSchemas['OptionLegDTO'] & { quotePair?: QuotePair };

interface PreRfqLegBuilder {
  productType: string;
  product: GenericLeg;
  missingAttributeOnPricing?: string;
  fieldsToBeEdited?: FieldsToBeEdited;
  childVanillaOption?: GenericLeg;
}

export interface PresetSetupData {
  setup: RfqSetup;
  hedges: MultiHedge[];
}

export const volBasedSpotFeedEditableFields = [
  'spot',
  'secondSpot',
  'forwardPointsBid',
  'forwardPointsAsk',
  'discountFactor',
  'rfqVolMargin',
  'rfqPremiumMargin',
  'premiumSettlementDate',
  'premiumValueType'
];

export const marginEditableFields = [
  'marginStyle',
  'margin',
  'marginBid',
  'marginAsk',
  'retrocessionFee'
];

export const volBasedEditableFields = [
  'optionType',
  'volBid',
  'vol1Bid',
  'vol2Bid',
  'volAsk',
  'vol1Ask',
  'vol2Ask'
];

export type RfqOutcome =
  | 'TRADED'
  | 'DECLINED'
  | 'TRADED AWAY'
  | 'DID NOT TRADE'
  | 'IDEA'
  | 'OTHER';

export const marginStyles = [
  'Amount Base',
  'Amount Term',
  'Pips',
  'Percent Base',
  'Percent Term'
];

export interface ShelvedQuote {
  eventQuote: ClientEventQuoteExtended | ClientQuoteEventV2['quote'];
  quote: RfqQuote;
  side: QuoteSide;
  indicative: boolean;
}

export type Margin = {
  margin: number;
  marginStyle: string;
};

export interface RfqWsErrorMessage {
  context: { message: string };
}

export function addRemoveVolSpread(
  addVolSpread: boolean,
  useWhiteLabelMargin: boolean,
  legComponents: LegComponent[]
): void {
  const volSpread = 'Vol Spread';
  if (!useWhiteLabelMargin) {
    return;
  }
  legComponents.forEach(legComponent => {
    const marginStyleVal = legComponent.form.value.marginStyle;
    let marginStyleOptions = clone(
      getAllAttributeOptions('marginStyle', legComponent.leg)
    );

    if (addVolSpread && !marginStyleOptions.includes(volSpread)) {
      marginStyleOptions.push(volSpread);
    } else if (!addVolSpread) {
      marginStyleOptions = marginStyleOptions.filter(
        field => field !== volSpread
      );
    }

    if (!marginStyleOptions.includes(marginStyleVal)) {
      optionsChangeHandler(
        legComponent.form,
        legComponent.leg,
        'marginStyle',
        marginStyleOptions,
        legComponent.form.get('marginStyle').enabled
          ? marginStyleOptions[0]
          : null
      );
    }
  });
}

export function v2ToQuoteMap(
  r: VolBasedRfqOptionPriceV2Result,
  setup: RfqSetup
): ClientQuoteEventV2 {
  const premiumBySettlementDate =
    r.portfolioResult.premiumBySettlementDate[
      r.portfolioResult.premiumPaymentDate
    ];
  return {
    premiumCurrency: setup.premium,
    legs: r.products,
    quote: {
      premiumCurrency: setup.premium,
      premiumAmountBid: premiumBySettlementDate.bid[setup.premium],
      premiumAmountOffer: premiumBySettlementDate.ask[setup.premium],
      description: null,
      premiumStyle: setup.premiumStyle as PremiumStyle,
      legs: r.products
    }
  };
}

export function hasFieldsChanged(
  strategyPriced: Payload,
  currVolBasedOptionProducts: GenericLeg[]
): boolean {
  const FieldsToCheckForChange = [
    'forwardPointsAsk',
    'forwardPointsBid',
    ...volBasedEditableFields
  ];

  return !!strategyPriced.products.find((product, i) => {
    if (FieldsToCheckForChange.find(field => product[field])) {
      return (
        !currVolBasedOptionProducts[i] ||
        FieldsToCheckForChange.find(
          field => product[field] !== currVolBasedOptionProducts[i][field]
        )
      );
    }
    return false;
  });
}

export function mappedEventScheduleKey(key: string): string {
  switch (key) {
    case 'notionalLong':
      return 'notional';
    case 'notionalShort':
      return 'notional2';
    case 'strikeShort':
      return 'strike2';
    case 'strikeLong':
      return 'strike';
    case 'leverageShort':
      return 'leverage2';
    case 'leverageLong':
      return 'leverage';
    case 'barrier':
    case 'lowerBarrier':
    case 'kiBarrier':
      return 'barrierLevel';
    case 'upperBarrier':
    case 'koBarrier':
      return 'barrierLevel2';
    default:
      return key;
  }
}

export function getLegOptionType(
  rfqTabShortName: string,
  legShortName: string
) {
  switch (rfqTabShortName) {
    case 'vanilla+VanillaStripMultiLeg':
      if (legShortName === 'vanilla') {
        return 'Vanilla';
      } else {
        return 'VanillaStrip';
      }
    case 'vanillaStripMultiLeg':
    case 'vanillaStrip':
      return 'VanillaStrip';
    case 'vanilla':
    case 'ndo':
    case 'dcd':
    case 'vanillaMultiLeg':
    case 'ndoMultiLeg':
      return 'Vanilla';
    case 'tpf':
    case 'simpleTarf':
      return 'TargetProfitForward';
    case 'simplePivotTarf':
      return 'PivotTargetProfitForward';
    case 'ki':
      return 'KnockIn';
    case 'ko':
      return 'KnockOut';
    case 'erki':
      return 'EuropeanKnockIn';
    case 'erko':
      return 'EuropeanKnockOut';
    case 'oneTouch':
      return 'OneTouch';
    case 'noTouch':
      return 'NoTouch';
    case 'oneTouchKnockOut':
      return 'OneTouchKnockOut';
    case 'instantOneTouch':
      return 'InstantOneTouch';
    case 'doubleNoTouch':
      return 'DoubleNoTouch';
    case 'doubleOneTouch':
      return 'DoubleTouch';
    case 'digital':
      return 'Digital';
    case 'doubleKnockIn':
      return 'DoubleKnockIn';
    case 'doubleKnockOut':
      return 'DoubleKnockOut';
    case 'simpleAccumulator':
      return 'SimpleAccumulator';
    default:
      return null;
  }
}

export function rfqLegAttributeDefaults(
  storedRfqLegAttributes: GenericLeg,
  prodSpec: ProductSpecification,
  legComponents: LegComponent[]
): GenericLeg {
  const defaults = {
    marginStyle: 'Pips',
    ...storedRfqLegAttributes
  };

  Object.keys(defaults).forEach(key => {
    const attribute = prodSpec.attributes.find(
      attribute => attribute.shortName === key
    );
    if (!attribute) {
      return;
    }

    if (attribute.type === 'enum' && attribute.value.includes(defaults[key])) {
      return;
    }

    switch (key) {
      case 'marginStyle':
        defaults.marginStyle = 'Pips';
        break;
      default:
        break;
    }
  });

  const containsDcd =
    legComponents.find(
      legComponent => legComponent.leg.productType === DUAL_CURRENCY_DEPOSIT
    ) || prodSpec.productType.shortName === DUAL_CURRENCY_DEPOSIT;

  if (containsDcd) {
    delete defaults.marginStyle;
  }
  return defaults;
}

export function handleDefaultPremiumInStyle(
  quotedPremiumSection: QuotedPremiumSection,
  eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2,
  side: QuoteSide,
  rfqDetailsDTO: RFQDetailsDTO,
  forwardPointsMultiplier: number
): void {
  if (!rfqDetailsDTO) {
    return;
  }

  switch (rfqDetailsDTO.premiumStyle) {
    case 'PercentBase':
    case 'PercentTerm':
      quotedPremiumSection.quotedPremium =
        side === 'bid' ? eventQuote.premiumBid : eventQuote.premiumOffer;
      quotedPremiumSection.showPercent = true;
      quotedPremiumSection.currency = eventQuote.premiumCurrency;
      break;
    case 'PipsTerm':
    case 'PipsBase':
      if (forwardPointsMultiplier) {
        quotedPremiumSection.quotedPremium =
          (side === 'bid' ? eventQuote.premiumBid : eventQuote.premiumOffer) *
          forwardPointsMultiplier;
        quotedPremiumSection.showPercent = false;
        quotedPremiumSection.isPips = true;
      }
      break;
  }
}

export function getTarfPremiumAsPercent(
  eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2,
  side: QuoteSide,
  legs: any[],
  quotePair: QuotePair
): number | null {
  if (legs.length !== 1) {
    throw new Error('TARFS must have only one leg');
  }
  const leg0 = legs[0];
  const schedule = leg0.eventSchedule as EventScheduleEntry[];

  const quotePremium =
    side === 'bid'
      ? eventQuote.premiumAmountBid
      : eventQuote.premiumAmountOffer;

  const rfqPremiumCurrency = eventQuote.premiumCurrency;
  const productCurrency = leg0.faceCurrency;
  const { base: baseCurrency, term: quoteCurrency } = quotePair;

  /*
    1. If the RFQ premium currency is the base currency (EUR for EURUSD):
    1a. If the product “currency” is also the base currency then we calculate Total Notional
    (in EUR) as the sum of the schedule’s notionals (EUR 2.5mn in the example above)
    1b. If the product “currency” is the quote currency (USD for EURUSD) then we calculate
    the Total Notional (in EUR) as the sum across the schedule of notional / strike
    (so 1mn / 1.1200 + 1.5mn / 1.1400 in the example above)
    1c. Else (which should not happen though) no calculation
  */
  let totalNotional: number = null;
  if (rfqPremiumCurrency === baseCurrency) {
    if (productCurrency === baseCurrency) {
      totalNotional = schedule.reduce((acc, val) => acc + val.notional, 0);
    } else if (productCurrency === quoteCurrency) {
      totalNotional = schedule.reduce(
        (acc, val) => acc + val.notional / val.strike,
        0
      );
    }
  }

  /*
    2. If the RFQ premium currency is the quote currency (USD for EURUSD):
    2a. If the product “currency” is the base currency then we calculate the Total Notional
    (in USD) as the sum across the schedule of notional x strike (so 1mn x 1.1200 + 1.5mn x 1.1400 in the example above)
    2b. If the product “currency” is also the quote currency then we calculate Total Notional
    (in USD) as the sum of the schedule’s notionals (USD 2.5mn in the example above)
    2c. Else (which should not happen though) no calculation
  */
  if (rfqPremiumCurrency === quoteCurrency) {
    if (productCurrency === baseCurrency) {
      totalNotional = schedule.reduce(
        (acc, val) => acc + val.notional * val.strike,
        0
      );
    } else if (productCurrency === quoteCurrency) {
      totalNotional = schedule.reduce((acc, val) => acc + val.notional, 0);
    }
  }

  return totalNotional ? (quotePremium / totalNotional) * 100 : null;
}

export function countdownColorCssClass(seconds: number) {
  if (seconds > 60) {
    return 'five-minutes';
  } else if (seconds <= 60 && seconds > 10) {
    return 'one-minute';
  } else {
    return 'ten-seconds';
  }
}

export function isProductNameATarf(productName: string): boolean {
  return (
    productName === 'tpf' ||
    productName === 'simpleTarf' ||
    productName === 'simplePivotTarf'
  );
}

export function getStrategy(
  shortName: string
): dvSchemas['RFQDetailsDTO']['strategy'] {
  switch (shortName) {
    case 'strangle':
      return 'Strangle';
    case 'spread':
    case 'ndoSpread':
      return 'VerticalSpread';
    case 'straddle':
      return 'Straddle';
    case 'vanilla':
    case 'ndo':
    case 'dcd':
      return 'Single';
    case 'ko':
    case 'ki':
    case 'erko':
    case 'erki':
    case 'vanillaMultiLeg':
    case 'ndoMultiLeg':
    case 'oneTouch':
    case 'oneTouchKnockOut':
    case 'noTouch':
    case 'instantOneTouch':
    case 'doubleNoTouch':
    case 'doubleOneTouch':
    case 'digital':
    case 'doubleKnockIn':
    case 'doubleKnockOut':
    case 'vanillaStrip':
    case 'custom':
    case 'vanillaStripMultiLeg':
    case 'vanilla+VanillaStripMultiLeg':
      return 'Custom';
    case 'riskReversal':
      return 'RiskReversal';
    case 'tpf':
    case 'simpleTarf':
    case 'simplePivotTarf':
      return 'TargetProfitForward';
    case 'simpleAccumulator':
      return 'Accumulator';
    default:
      return null;
  }
}

export function getNotionalField(leg: KeyValue): Notional {
  if (leg.notional) {
    return 'notional';
  } else if (leg.notional1) {
    return 'notional1';
  } else if (leg.notional2) {
    return 'notional2';
  } else {
    return null;
  }
}

export function rfqProductApiName(
  legs: KeyValue[],
  shortName: string
): RfqProductApiName {
  const strategy = getStrategy(shortName);
  if (strategy === 'Custom') {
    return 'custom';
  }

  switch (shortName) {
    case 'strangle':
      return 'strangle';
    case 'spread':
    case 'ndoSpread':
      return 'spread';
    case 'straddle':
      return 'straddle';
    case 'vanilla':
    case 'ndo':
    case 'dcd':
      return legs?.[0]?.call ? 'call' : 'put';
    case 'riskReversal':
      return 'riskreversal';
    case 'tpf':
    case 'simpleTarf':
    case 'simplePivotTarf':
      return 'tpf';
    case 'simpleAccumulator':
      return 'custom';
    default:
      throw Error(`Unknown product name: ${shortName}`);
  }
}

export function handleScheduleFormatting(leg: GenericLeg) {
  return leg.schedule.map(scheduleRow => {
    const newScheduleRow: Record<string, string | number> = {};
    Object.entries(scheduleRow).forEach(([rowKey, rowValue]) => {
      const mappedKey = mappedEventScheduleKey(rowKey);
      switch (rowKey) {
        case 'fixingDate':
        case 'settlementDate':
        case 'expiryDate':
          newScheduleRow[mappedKey] = +dayjs(rowValue + ' GMT');
          break;
        case 'strike':
        case 'notional':
        case 'leverage':
        case 'notionalLong':
        case 'notionalShort':
        case 'strikeShort':
        case 'strikeLong':
        case 'leverageShort':
        case 'leverageLong':
        case 'pivot':
          newScheduleRow[mappedKey] = rowValue.toString().replace(/,/g, '');
          break;
        case 'barrier':
        case 'lowerBarrier':
        case 'upperBarrier':
          if (leg[barrierToBarrierType(rowKey)] !== 'None') {
            newScheduleRow[mappedKey] =
              Utilities.cleanFormattedNumeric(rowValue);
          }
          break;
        case 'kiBarrier':
          if (Utilities.isNotBlank(rowValue)) {
            newScheduleRow[mappedKey] =
              Utilities.cleanFormattedNumeric(rowValue);
            newScheduleRow.barrierObservationType = 'European';
            newScheduleRow.barrierType = 'In';
          }
          break;
        case 'koBarrier':
          if (
            leg[barrierToBarrierType(rowKey)] !== 'None' &&
            Utilities.isNotBlank(rowValue)
          ) {
            newScheduleRow[mappedKey] =
              Utilities.cleanFormattedNumeric(rowValue);
            if (leg.koBarrierType === 'EKO') {
              newScheduleRow.barrierObservationType2 = 'European';
              newScheduleRow.barrierType2 = 'Out';
            }
          }
          break;
      }
    });
    return newScheduleRow;
  });
}

export function setBarrierType(
  dtoLeg: OptionLegDTO,
  shortName: string
): OptionLegDTO {
  let barrierType = dtoLeg.barrierType;
  switch (shortName) {
    case 'doubleNoTouch':
      barrierType = 'Out';
      break;
    case 'doubleOneTouch':
      barrierType = 'In';
      break;
  }
  return { ...dtoLeg, barrierType };
}

export function getHedgeType(hedge: string): NewVolsHedgeType {
  switch (hedge) {
    case 'Spot':
      return 'spot';
    case 'Custom Spot':
      return 'customSpot';
    case 'Forward':
      return 'forward';
    case 'Custom Forward':
      return 'customForward';
    case 'None':
      return 'None';
    default:
      return null;
  }
}

export function validProduct(selectedLegs: { form: AbstractControl }[]) {
  return (product: Product, index: number): boolean => {
    const form = selectedLegs[index].form;
    const { forwardPointsBid, forwardPointsAsk, volAsk, volBid } = product;
    return (
      volBid != null &&
      volAsk != null &&
      volBid <= volAsk &&
      forwardPointsBid?.length &&
      forwardPointsAsk?.length &&
      Utilities.cleanFormattedNumeric(forwardPointsBid) <=
        Utilities.cleanFormattedNumeric(forwardPointsAsk) &&
      form.valid
    );
  };
}

export function formattingForTpf(
  dtoLeg: OptionLegDTO,
  leg: KeyValue,
  shortName: string
): OptionLegDTO {
  if (!isProductNameATarf(shortName)) {
    return dtoLeg;
  }
  dtoLeg = clone(dtoLeg);
  const eki = 'EKI';
  if (
    leg.barrierType === eki ||
    leg.upperBarrierType === eki ||
    leg.lowerBarrierType === eki
  ) {
    dtoLeg.barrierObservationType = 'European';
    dtoLeg.barrierType = 'In';
  }

  dtoLeg.strikeSpecified = false;
  dtoLeg.holdStrikeOnResubmit = false;
  dtoLeg.faceCurrency = leg.currency;

  if (!dtoLeg.invertedTradeDirection) {
    dtoLeg.invertedTradeDirection = false;
  }
  delete dtoLeg.strike;
  return dtoLeg;
}

export function formattingForAccumulator(
  dtoLeg: OptionLegDTO,
  leg: KeyValue,
  shortName: string
): OptionLegDTO {
  if (shortName !== 'simpleAccumulator') {
    return dtoLeg;
  }
  dtoLeg = clone(dtoLeg);
  dtoLeg.faceCurrency = leg.currency;
  dtoLeg.strikeSpecified = false;
  return dtoLeg;
}

export function formatLegForDto(
  attributeName: string,
  dtoLeg: OptionLegDTO,
  leg: GenericLeg,
  rfqTabShortName: string,
  isFirstGenExotic: boolean
): OptionLegDTO {
  dtoLeg = clone(dtoLeg);
  switch (attributeName) {
    case 'productType':
      const legOptionType = getLegOptionType(rfqTabShortName, leg.legShortName);
      if (legOptionType) {
        dtoLeg.legOptionType = legOptionType;
      }
      break;
    case 'call':
      dtoLeg['call'] = leg[attributeName];
      break;
    case 'barrierType':
      if (bypassBarrierTypeMapping(rfqTabShortName)) {
        break;
      }

      if (!isFirstGenExotic) {
        break;
      }
      if (
        leg[attributeName] === 'UpAndOut' ||
        leg[attributeName] === 'UpAndIn' ||
        leg[attributeName] === 'Up'
      ) {
        dtoLeg.barrierDirection = 'Up';
      } else if (
        leg[attributeName] === 'DownAndOut' ||
        leg[attributeName] === 'DownAndIn' ||
        leg[attributeName] === 'Down'
      ) {
        dtoLeg.barrierDirection = 'Down';
      } else {
        throw `Barrier type mapping doesn't exist`;
      }

      break;
    case 'barrier':
    case 'upperBarrier':
      if (isFirstGenExotic) {
        dtoLeg.barrierLevel = Utilities.cleanFormattedNumeric(
          leg[attributeName]
        );
      }
      break;
    case 'lowerBarrier':
    case 'koBarrier':
      if (isFirstGenExotic) {
        dtoLeg.barrierLevel2 = Utilities.cleanFormattedNumeric(
          leg[attributeName]
        );
      }
      break;
    case 'cross':
      dtoLeg = handleCrossFormatting(
        leg,
        dtoLeg,
        rfqTabShortName,
        isFirstGenExotic
      );
      break;
    case 'notional':
      if (isFirstGenExotic) {
        if (leg.counterNotional !== undefined) {
          const notionalToUse =
            leg.notionalFieldsToUse[getNotionalField(leg)] === 'notional'
              ? 'counterNotional'
              : 'notional';
          dtoLeg.notionalAmount = formattedNumberToNumber(leg[notionalToUse]);
        } else {
          dtoLeg.notionalAmount = formattedNumberToNumber(leg.notional);
        }
      }
      break;
    case 'expiryDate':
    case 'settlementDate':
      dtoLeg[attributeName] = +dayjs(leg[attributeName] + ' GMT');
      break;
    case 'schedule':
      if (leg.schedule) {
        dtoLeg.eventSchedule = handleScheduleFormatting(leg);
      }
      break;
    case 'strike':
    case 'strikeLong':
      dtoLeg['strike'] = Utilities.cleanFormattedNumeric(leg[attributeName]);
      dtoLeg.strikeSpecified = true;
      break;
    case 'optionType':
      dtoLeg.call = leg[attributeName] === 'Call';
      break;
    case 'targetProfit':
      dtoLeg['targetProfitAmount'] = parseFloat(
        leg[attributeName].toString().replace(/,/g, '')
      );
      break;
    case 'targetProfitUnit':
      if (leg[attributeName] === 'Spot') {
        dtoLeg.targetProfitUnit = 'Spot';
      } else if (leg[attributeName] === leg.cross.substring(0, 3)) {
        dtoLeg.targetProfitUnit = 'Currency1';
      } else if (leg[attributeName] === leg.cross.substring(3, 6)) {
        dtoLeg.targetProfitUnit = 'Currency2';
      } else {
        throw 'Target Profit Unit cannot be mapped';
      }
      break;
    case 'targetProfitType':
      dtoLeg['targetProfitType'] = leg[attributeName]
        .toString()
        .replace(/\s+/g, '');
      break;
    case 'targetProfitAdjustmentType':
      if (leg[attributeName] === 'Adjust Strike') {
        dtoLeg.targetProfitAdjustmentType = 'AdjustStrike';
      } else if (leg[attributeName] === 'Adjust Notional') {
        dtoLeg.targetProfitAdjustmentType = 'AdjustNotional';
      } else if (
        leg[attributeName].substring(
          leg[attributeName].lastIndexOf('(') + 1,
          leg[attributeName].lastIndexOf(')')
        ) === leg.cross.substring(0, 3)
      ) {
        dtoLeg.targetProfitAdjustmentType = 'CashCurrency1';
      } else if (
        leg[attributeName].substring(
          leg[attributeName].lastIndexOf('(') + 1,
          leg[attributeName].lastIndexOf(')')
        ) === leg.cross.substring(3, 6)
      ) {
        dtoLeg.targetProfitAdjustmentType = 'CashCurrency2';
      } else if (leg.knockOutType === 'Exact') {
        throw 'Target Profit Adjustment Type can not be mapped';
      }
      break;
    case 'koType':
      dtoLeg.knockOutType = leg[attributeName];
      break;
    case 'direction':
      setStructureDirection(rfqTabShortName, leg[attributeName], dtoLeg);
      break;
    case 'koBarrierType':
      if (leg.koBarrierType === 'AKO') {
        throw `AKO as a knock out barrier type is not supported currently on simple Accumulators`;
      }
      break;
  }
  return dtoLeg;
}

export function setInvertedTradeDirection(
  dtoLeg: OptionLegDTO,
  leg: KeyValue,
  isMultiLegged: boolean
): OptionLegDTO {
  dtoLeg = clone(dtoLeg);
  if (isMultiLegged && (leg.position === 'Unwind' || leg.position === 'Sell')) {
    dtoLeg.invertedTradeDirection = true;
  }
  return dtoLeg;
}

export function putCallAmountHandler(
  leg: KeyValue,
  dtoLeg: OptionLegDTO,
  notionalField: Notional,
  shortName: string
): OptionLegDTO {
  dtoLeg = clone(dtoLeg);
  switch (shortName) {
    case 'dcd':
      if (leg.notionalFieldsToUse.notional === 'counterNotional') {
        dtoLeg = setPutCallAmountUsingNotionalOnly(leg, dtoLeg);
      } else {
        dtoLeg[dtoLeg.call ? 'putAmount' : 'callAmount'] = leg.counterNotional;
        dtoLeg[dtoLeg.call ? 'callAmount' : 'putAmount'] =
          leg.counterNotional / leg.strike;
      }
      break;
    case 'oneTouch':
    case 'noTouch':
    case 'oneTouchKnockOut':
    case 'instantOneTouch':
    case 'doubleNoTouch':
    case 'doubleOneTouch':
      const notional = formattedNumberToNumber(leg[notionalField]);
      dtoLeg.callAmount = notional;
      dtoLeg.putAmount = notional;
      break;
    case 'digital':
      const notionalVal = formattedNumberToNumber(leg[notionalField]);
      dtoLeg.callAmount = notionalVal;
      dtoLeg.putAmount = notionalVal * leg.strike;
      break;
    case 'vanillaStrip':
      const notionalValue = Number(leg.notional.replace(/[^0-9.]/g, '') || 0);
      const strike = Number(Utilities.cleanFormattedNumeric(leg.strike) || 0);
      const baseCurrency = leg.cross.substring(0, 3);
      const isBaseCurrency = leg.currency === baseCurrency;
      dtoLeg.callAmount =
        leg.optionType === 'Call'
          ? isBaseCurrency
            ? notionalValue
            : Number((notionalValue / strike).toFixed(2))
          : isBaseCurrency
            ? notionalValue * strike
            : notionalValue;
      dtoLeg.putAmount =
        leg.optionType === 'Call'
          ? isBaseCurrency
            ? notionalValue * strike
            : notionalValue
          : isBaseCurrency
            ? notionalValue
            : Number((notionalValue / strike).toFixed(2));
      break;
    default:
      if (
        leg[notionalField] &&
        leg.counterNotional &&
        dtoLeg.call !== undefined
      ) {
        dtoLeg[dtoLeg.call ? 'callAmount' : 'putAmount'] =
          formattedNumberToNumber(leg[notionalField]);

        dtoLeg[dtoLeg.call ? 'putAmount' : 'callAmount'] =
          formattedNumberToNumber(leg.counterNotional);
      } else if (leg.notional && dtoLeg.call !== null) {
        dtoLeg = setPutCallAmountUsingNotionalOnly(leg, dtoLeg);
      }
      break;
  }

  return dtoLeg;
}
export function setDisplayVols(
  inquote: Readonly<RfqQuote>,
  eventQuote: Readonly<ClientEventQuoteExtended | ClientEventQuoteExtendedV2>,
  legs: Readonly<RfqTabLegs[]>,
  shortName: string,
  side: string,
  volBased: boolean
): RfqQuote {
  const quote: RfqQuote = clone(inquote);
  const useOnlyOne = useOnlyOneVolRow(eventQuote.legs.length, shortName);

  quote.vol = {
    left: makeVols(
      useOnlyOne
        ? [eventQuote.legs[0].volBid]
        : eventQuote.legs.map(leg => leg.volBid),
      quote.legPremiums,
      quote.legCashPremiums,
      'bid',
      quote.bidAsk.currency,
      quote.legsInverted,
      quote.cashToPremiumStyleDividingAmounts
    ),
    right: makeVols(
      useOnlyOne
        ? [
            eventQuote.legs[0].volOffer ||
              (eventQuote.legs[0] as GenericLeg).volAsk
          ]
        : eventQuote.legs.map(
            leg => leg.volOffer || (leg as unknown as GenericLeg).volAsk
          ),
      quote.legPremiums,
      quote.legCashPremiums,
      'ask',
      quote.bidAsk.currency,
      quote.legsInverted,
      quote.cashToPremiumStyleDividingAmounts
    )
  };

  if (
    ['riskReversal', 'spread', 'ndoSpread'].includes(shortName) &&
    !volBased
  ) {
    quote.vol.left.splice(2);
    quote.vol.right.splice(2);
    const tempLeftVol = quote.vol.left[1];
    const tempRightVol = quote.vol.right[1];
    const [leg0, leg1] = legs;

    if (leg0.leg.position === 'Sell' || leg0.leg.position1 === 'Sell') {
      quote.vol.left = [tempRightVol, quote.vol.left[0]];
      quote.vol.right = [tempLeftVol, quote.vol.right[0]];
    } else if (leg1?.leg.position === 'Sell' || leg0.leg.position2 === 'Sell') {
      quote.vol.left[1] = tempRightVol;
      quote.vol.right[1] = tempLeftVol;
    }
  }

  return quote;
}

// Impure

export function setVolsForwards(
  eventQuote: ClientEventQuoteExtended,
  rfqShortName: string,
  legs: LegComponent[],
  forwardMultiplier: number
): void {
  setVols(eventQuote, rfqShortName, legs);
  setFwdPoints(eventQuote, rfqShortName, legs, forwardMultiplier);
}

export function setVols(
  eventQuote: ClientEventQuoteExtended,
  rfqShortName: string,
  legs: LegComponent[]
) {
  legs.forEach((leg, index) => {
    callHandleVol(
      leg,
      eventQuote,
      rfqShortName === 'riskReversal' ? '1' : '',
      index
    );

    if (rfqShortName === 'riskReversal') {
      callHandleVol(leg, eventQuote, '2', 1);
    }
  });
}

export function setFwdPoints(
  eventQuote: ClientEventQuoteExtended,
  rfqShortName: string,
  legs: LegComponent[],
  forwardMultiplier: number
): void {
  eventQuote.legs.forEach((quoteLeg, index) => {
    let hydraLegIndex = index;
    if (
      legs.length === 1 &&
      (rfqShortName === 'straddle' ||
        rfqShortName === 'riskReversal' ||
        rfqShortName === 'strangle')
    ) {
      hydraLegIndex = 0;
      if (quoteLeg.inverted) {
        return;
      }
    } else if (eventQuote.legs.length !== legs.length) {
      const errorMessage =
        'Number of Hydra legs and quote legs are not the same.';
      this.alertsService.addAlert({ message: errorMessage });
      throw new Error(errorMessage);
    }

    if (
      quoteLeg.forwardBid !== null &&
      quoteLeg.spotBid !== null &&
      quoteLeg.forwardOffer !== null &&
      quoteLeg.spotOffer !== null
    ) {
      const forwardPointsBid =
        (quoteLeg.forwardBid - quoteLeg.spotBid) * forwardMultiplier;
      const forwardPointsAsk =
        (quoteLeg.forwardOffer - quoteLeg.spotOffer) * forwardMultiplier;

      if (forwardPointsAsk < forwardPointsBid) {
        return;
      }

      ['forwardPointsBid', 'forwardPointsAsk'].forEach(
        (field: 'forwardPointsBid' | 'forwardPointsAsk') =>
          legs[hydraLegIndex].legFwdVolService.updateForwardPoints(
            field,
            field === 'forwardPointsAsk' ? forwardPointsAsk : forwardPointsBid,
            null,
            field
          )
      );
    }
  });
}

export function indicativeQuoteMaker(
  side: 'bid' | 'ask',
  priceResult: Pricing,
  premiumCurrency: string,
  base: string,
  premiumType: string
): IndicativeQuote {
  const isPremiumCurrencyBase = premiumCurrency === base;
  const amount = indicativeAmount(
    premiumCurrency,
    base,
    priceResult.price,
    premiumType
  );
  const quotedPremiumValue = quotedPremium(
    premiumCurrency,
    base,
    priceResult.price,
    premiumType
  );
  if (!amount) {
    return null;
  }

  const multiplyQuotedPremiumBy = isPremiumCurrencyBase ? 100 : 1;

  return {
    bidAsk: {
      type: priceResult.headers[side],
      amount: amount[side] !== '' ? +amount[side] : null,
      currency:
        priceResult[isPremiumCurrencyBase ? 'baseCurrency' : 'quoteCurrency']
    },
    quotedPremiumSection: {
      quotedPremium: quotedPremiumValue?.[side]
        ? +quotedPremiumValue[side] * multiplyQuotedPremiumBy
        : null,
      showPercent: isPremiumCurrencyBase,
      currency: isPremiumCurrencyBase ? premiumCurrency : 'pips'
    },
    status: 'Indicative'
  };
}

/** PRIVATE *******************************************/

// Impure
function callHandleVol(
  leg: LegComponent,
  eventQuote: ClientEventQuoteExtended,
  number: '' | '1' | '2',
  index: number
) {
  const askVolControl = getAttributeControlFromKey(
    `vol${number}Ask`,
    leg.legHelperService.leg.attributesAsFormControls
  );
  const bidVolControl = getAttributeControlFromKey(
    `vol${number}Bid`,
    leg.legHelperService.leg.attributesAsFormControls
  );

  const askVolReadonly = askVolControl?.readOnly;
  const bidVolReadonly = bidVolControl?.readOnly;

  const vols = [
    {
      strikeVol: {
        mid: 0,
        bid: eventQuote.legs[index].volBid,
        ask: eventQuote.legs[index].volOffer
      }
    }
  ];

  leg.legFwdVolService.handleImpliedVolatilityResponse({
    askBidMidNumber: vols[0].strikeVol,
    timestamp: '',
    volNumber: number,
    shouldEventBeEmitted: false
  });

  if (askVolControl && bidVolControl) {
    askVolControl.readOnly = askVolReadonly;
    bidVolControl.readOnly = bidVolReadonly;
  }
}

function handleCrossFormatting(
  leg: KeyValue,
  dtoLeg: OptionLegDTO,
  rfqTabShortName: string,
  isFirstGenExotic: boolean
): OptionLegDTO {
  const quotePair = crossToPair(leg.cross);
  const faceCurrency = getFaceCurrency(leg, rfqTabShortName, isFirstGenExotic);
  return { ...dtoLeg, quotePair, faceCurrency };
}

function setPutCallAmountUsingNotionalOnly(
  // The fields are flagged as optional here to match the DTO spec.
  leg: { notional?: string; strike?: number },
  dtoLeg: OptionLegDTO
): OptionLegDTO {
  const call = dtoLeg.call;
  return {
    ...dtoLeg,
    [call ? 'callAmount' : 'putAmount']: formattedNumberToNumber(leg.notional),
    [call ? 'putAmount' : 'callAmount']:
      formattedNumberToNumber(leg.notional) * leg.strike
  };
}

function getFaceCurrency(
  leg: Readonly<KeyValue>,
  rfqTabShortName: string,
  isFirstGenExotic: boolean
) {
  if (
    (isFirstGenExotic && leg.currency) ||
    leg.legShortName === VANILLA_STRIP
  ) {
    return leg.currency;
  } else if (leg.faceCurrency && rfqTabShortName === 'dcd') {
    return leg.faceCurrency; // if the leg has a face currency field use it only on dcd's
  }
  const notionalCalculated = leg.notionalFieldsToUse[getNotionalField(leg)];

  if (!notionalCalculated || notionalCalculated === 'counterNotional') {
    return leg.cross.substring(0, 3);
  } else {
    return leg.cross.substring(3, 6);
  }
}

function bypassBarrierTypeMapping(shortName: string): boolean {
  switch (shortName) {
    case 'erki':
    case 'erko':
    case 'doubleKnockIn':
    case 'doubleKnockOut':
      return true;
    default:
      return false;
  }
}

function barrierToBarrierType(
  barrier: 'barrier' | 'lowerBarrier' | 'upperBarrier' | 'koBarrier'
): 'barrierType' | 'upperBarrierType' | 'lowerBarrierType' | 'koBarrierType' {
  switch (barrier) {
    case 'barrier':
      return 'barrierType';
    case 'lowerBarrier':
      return 'lowerBarrierType';
    case 'upperBarrier':
      return 'upperBarrierType';
    case 'koBarrier':
      return 'koBarrierType';
  }
}

function useOnlyOneVolRow(numberOfLegs: number, shortName: string) {
  return numberOfLegs === 1 || shortName === 'straddle';
}

function makeVols(
  eventVols: Readonly<any[]>,
  legPremiums: Readonly<BidMidAsk[]>,
  legCashPremiums: Readonly<BidMidAsk[]>,
  side: string,
  currency: string,
  legsInverted: Readonly<boolean[]>,
  cashToPremiumStyleDividingAmounts: number[]
): any[] {
  return eventVols
    .filter(v => v != null)
    .map((value, i) => {
      const buyOrSell = side === 'bid' ? 'sell' : 'buy';

      let ret: Vol = {
        value,
        buyOrSell
      };
      const legPremium = legPremiums?.[i][side][currency];
      const legCashPremium = legCashPremiums?.[i][side][currency];
      const cashToPremiumStyleDividingAmount =
        cashToPremiumStyleDividingAmounts?.[i];
      let payOrReceive: string;
      if (legPremium != null) {
        if (side === 'bid') {
          payOrReceive = legPremium < 0 ? 'Pay' : 'Receive';
        } else {
          payOrReceive = legPremium < 0 ? 'Receive' : 'Pay';
        }
        let premiumBuyOrSell: string;
        // buy is green, sell is red
        if (side === 'bid') {
          // left
          premiumBuyOrSell = legsInverted[i] ? 'buy' : 'sell';
        } else {
          premiumBuyOrSell = legsInverted[i] ? 'sell' : 'buy';
        }

        ret = {
          ...ret,
          premium: {
            value: legPremium,
            cashValue: legCashPremium,
            payOrReceive,
            buyOrSell: premiumBuyOrSell,
            cashToPremiumStyleDividingAmount
          }
        };
      }

      return ret;
    });
}

export function getRfqDto(
  inlegs: KeyValue[],
  shortName: string,
  isFirstGenExotic: boolean
): OptionLegDTO[] {
  const isMultiLegged = inlegs.length > 1;
  const legs = inlegs.map(leg => {
    let dtoLeg: OptionLegDTO = {
      delta: 0,
      strikeType: 'MANUAL'
    };
    Object.keys(leg).forEach(attributeName => {
      // go through leg fields
      dtoLeg = formatLegForDto(
        attributeName,
        dtoLeg,
        leg,
        shortName,
        isFirstGenExotic
      );
    });

    dtoLeg = setBarrierType(dtoLeg, shortName);
    dtoLeg = putCallAmountHandler(
      leg,
      dtoLeg,
      getNotionalField(leg),
      shortName
    );
    dtoLeg = setInvertedTradeDirection(dtoLeg, leg, isMultiLegged);
    dtoLeg = formattingForTpf(dtoLeg, leg, shortName);
    return dtoLeg;
  });
  return legs;
}

// Make deep copy of an object, but round all numbers to N d.p.
// (They are converted to strings but that doesn't matter here.)
export function deepCopyNdp(obj: any, dp = 4): any {
  function makeReplacer(dp: number) {
    return function replacer(_: string, value: any) {
      if (typeof value === 'number') {
        // We only want to handle explicit decimals
        if (value !== parseInt(value as any)) {
          return value.toFixed(dp);
        }
      }
      return value;
    };
  }
  return JSON.parse(JSON.stringify(obj, makeReplacer(dp)));
}

function quotedPremium(
  premiumCurrency: string,
  base: string,
  price: Price,
  premiumType: string
): MidBidAsk {
  if (premiumCurrency === base) {
    const fieldToUse = premiumType.toLowerCase().includes('spot')
      ? 'premiumBaseCurrencyInPercent'
      : 'forwardpremiumBaseCurrencyInPercent';

    if (!price[fieldToUse]) {
      console.warn(`missing ${fieldToUse} for indicative pricing`);
    }
    return price[fieldToUse];
  } else {
    const fieldToUse = premiumType.toLowerCase().includes('spot')
      ? 'premiumQuoteCurrencyPips'
      : 'forwardpremiumQuoteCurrencyPips';
    if (!price[fieldToUse]) {
      console.warn(`missing ${fieldToUse} for indicative pricing`);
    }
    return price[fieldToUse];
  }
}

function indicativeAmount(
  premiumCurrency: string,
  base: string,
  price: Price,
  premiumType: string
): MidBidAsk {
  if (premiumCurrency === base) {
    const fieldToUse = premiumType.toLowerCase().includes('spot')
      ? 'premiumBaseCurrency'
      : 'forwardpremiumBaseCurrency';

    if (!price[fieldToUse]) {
      console.warn(`missing ${fieldToUse} for indicative pricing`);
    }
    return price[fieldToUse];
  } else {
    const fieldToUse = premiumType.toLowerCase().includes('spot')
      ? 'premiumQuoteCurrency'
      : 'forwardpremiumQuoteCurrency';

    if (!price[fieldToUse]) {
      console.warn(`missing ${fieldToUse} for indicative pricing`);
    }
    return price[fieldToUse];
  }
}

export function singleLegTitleMapper(
  productType: string,
  usePerseus: boolean,
  allowWhenBreakingDownLongName = false
): ProductName {
  switch (productType) {
    // maps to correct shortname
    case 'accumulator':
      return null;
    case 'vanilla':
    case 'vanillaoption':
      return {
        shortName: 'vanilla',
        longName: 'Vanilla'
      };
    case VANILLA_STRIP:
      return { shortName: 'vanillaStrip', longName: 'Vanilla Strip' };

    case RISK_REVERSAL_STRIP:
      return allowWhenBreakingDownLongName
        ? {
            shortName: RISK_REVERSAL_STRIP,
            longName: 'Risk Reversal Strip'
          }
        : null;
    case 'newdcd':
      return { shortName: 'dcd', longName: 'Dual-Currency Deposit' };
    case 'riskreversal':
      return { shortName: 'riskReversal', longName: 'Risk Reversal' };
    case 'strangle':
      return { shortName: 'strangle', longName: 'Strangle' };
    case 'straddle':
      return { shortName: 'straddle', longName: 'Straddle' };
    case 'tpf':
      return { shortName: 'tpf', longName: 'TPF' };
    case 'simplepivottpf':
      return { shortName: 'simplePivotTarf', longName: 'Simple Pivot Tarf' };
    case 'simpletarf':
      return { shortName: 'simpleTarf', longName: 'Simple Tarf' };
    case 'ko':
      return { shortName: 'ko', longName: 'KO' };
    case 'ki':
      return { shortName: 'ki', longName: 'KI' };
    case 'erko':
      return { shortName: 'erko', longName: 'European Knock Out' };
    case 'erki':
      return { shortName: 'erki', longName: 'European Knock In' };
    case 'doubleknockin':
      return { shortName: 'doubleKnockIn', longName: 'Double Knock In' };
    case 'doubleknockout':
      return { shortName: 'doubleKnockOut', longName: 'Double Knock Out' };
    case 'onetouch':
      return { shortName: 'oneTouch', longName: 'One Touch' };
    case 'onetouchko':
      return { shortName: 'oneTouchKnockOut', longName: 'One Touch Knock Out' };
    case 'notouch':
      return { shortName: 'noTouch', longName: 'No Touch' };
    case 'instantonetouch':
      return { shortName: 'instantOneTouch', longName: 'Instant One Touch' };
    case 'doublenotouch':
      return { shortName: 'doubleNoTouch', longName: 'Double No Touch' };
    case 'doubleonetouch':
      return { shortName: 'doubleOneTouch', longName: 'Double One Touch' };
    case 'digital':
      return { shortName: 'digital', longName: 'Digital' };
    case 'ndo':
      return { shortName: 'ndo', longName: 'NDO' };
    case 'simpleaccumulator':
      return { shortName: 'simpleAccumulator', longName: 'Simple Accumulator' };
    default:
      return usePerseus
        ? {
            shortName: 'custom',
            longName: 'Custom'
          }
        : null;
  }
}

export function getBidOfferLabel(
  side: 'bid' | 'offer',
  premiumValue: number
): 'Pay' | 'Receive' {
  if (side === 'bid') {
    return premiumValue < 0 ? 'Pay' : 'Receive';
  } else {
    return premiumValue < 0 ? 'Receive' : 'Pay';
  }
}

export function positionsHandler(
  rfqShortName: string,
  legs: RfqBuilderLeg[]
): RfqTabPosition {
  if (!legs.length) {
    return null;
  }
  let positions = [];
  const everyLegHasPositions = legs.every(x => x.product.position);

  if (!everyLegHasPositions) {
    return getLegClientActionsHeader(['Buy']);
  } else {
    positions = legs.map(x => x.product.position);
  }

  if (legs.length > 1) {
    return getLegClientActionsHeader(positions);
  }

  if (rfqShortName === 'dcd') {
    return { bid: 'Margin', offer: '' };
  } else if (isProductNameATarf(rfqShortName)) {
    const positions = getLegClientActionsHeader(['Enter']);
    positions.bid = '';
    return positions;
  }

  return getLegClientActionsHeader(positions);
}

export function getLegClientActionsHeader(
  positions: Position[]
): RfqTabPosition {
  const convertedPositions = positions.map(position =>
    positionToBuySell(position)
  );
  const joinWith = ' / ';
  const offerHeader = convertedPositions.join(joinWith);
  const bidHeader = reversePositionAction(convertedPositions).join(joinWith);

  return {
    bid: `Client ${bidHeader}`,
    offer: `Client ${offerHeader}`
  };
}

function reversePositionAction(positions: PositionAction[]): PositionAction[] {
  return positions.map(position => {
    switch (position) {
      case 'Buys':
        return 'Sells';
      case 'Sells':
        return 'Buys';
      default:
        return '';
    }
  });
}

function positionToBuySell(value: RfqPosition): PositionAction {
  switch (value) {
    case 'Buy':
    case 'Enter':
    case 'Two-way':
      return 'Buys';
    case 'Sell':
    case 'Unwind':
      return 'Sells';
    default:
      return '';
  }
}

export function legComponentsToRfqBuilderLegs(
  legs: LegComponent[],
  brokenDownProducts?: Record<string, any>[][]
): RfqBuilderLeg[] {
  const formattedLeg = legs.map(leg => {
    return {
      productType: leg.leg.productType,
      product: leg.form.value,
      missingAttributeOnPricing: leg.leg.missingAttributeOnPricing,
      fieldsToBeEdited: leg.fieldsToBeEdited,
      childVanillaOption: leg.leg.pricedLeg?.childVanillaOption
    };
  });

  return legsToRfqBuilderLegs(formattedLeg, brokenDownProducts);
}

export function legsToRfqBuilderLegs(
  legs: PreRfqLegBuilder[],
  brokenDownProducts?: Record<string, any>[][]
): RfqBuilderLeg[] {
  return legs
    .map((leg, index) => {
      if (brokenDownProducts) {
        return brokenDownProducts[index].map(product => {
          return {
            productType: VANILLA,
            product,
            missingAttributeOnPricing: leg.missingAttributeOnPricing,
            fieldsToBeEdited: leg.fieldsToBeEdited,
            childVanillaOption: leg.childVanillaOption
          };
        });
      } else {
        return {
          productType: leg.productType,
          product: leg.product,
          missingAttributeOnPricing: leg.missingAttributeOnPricing,
          fieldsToBeEdited: leg.fieldsToBeEdited,
          childVanillaOption: leg.childVanillaOption
        };
      }
    })
    .flat();
}

export function shouldBreakdownStrategy(
  checkedLegs: LegComponent[],
  breakdownFlag: boolean
): boolean {
  const shouldBreakdown =
    breakdownFlag ||
    checkedLegs.length > 1 ||
    !singleLegTitleMapper(checkedLegs[0].leg.productType, false, true); /*
    incase it's a product like vanilla strip that
    requires it to be brokendown by the backend */

  const productsBreakable = checkedLegs.every(x => {
    switch (x.leg.productType) {
      case RISK_REVERSAL_STRIP:
      case VANILLA:
      case STRANGLE:
      case STRADDLE:
      case RISK_REVERSAL:
        return true;
      default:
        return false;
    }
  });

  const productsNotAllVanillas = !checkedLegs.every(
    x => x.leg.productType === VANILLA
  );

  return shouldBreakdown && productsBreakable && productsNotAllVanillas;
}

export function breakDownLongName(legs: LegComponent[]): string {
  if (legs.every(x => x.leg.productType === legs[0].leg.productType)) {
    return `${
      singleLegTitleMapper(legs[0].leg.productType, false, true).longName
    } ${legs.length > 1 ? 'Multi-Leg' : ''}`;
  }
  return 'Multi-Leg';
}

export function changeInCross(
  previousLeg: GenericLeg,
  currentLeg: GenericLeg
): boolean {
  if (!previousLeg) {
    return true;
  }
  return (
    previousLeg.cross !== currentLeg.cross ||
    `${currentLeg.depositCurrency}${currentLeg.alternativeCurrency}` !==
      `${previousLeg.depositCurrency}${previousLeg.alternativeCurrency}`
  );
}

export function allFaceCurrenciesTheSame(
  selectedLegs: LegComponent[]
): boolean {
  const leg0FormValue = selectedLegs[0].form.value;
  return selectedLegs.every(
    (leg, _, arr) => leg.form.value.faceCurrency === leg0FormValue.faceCurrency
  );
}

export function enableDisableMarginTools(
  legComponents: LegComponent[],
  useWhiteLabelMargin: boolean,
  editableSettings: { editableFields: string[]; lockWhileActive: string[] },
  staticRfq: boolean,
  containsDcd: boolean
): void {
  if (!useWhiteLabelMargin) {
    return;
  }

  legComponents.forEach(legComponent => {
    const form = legComponent.form;
    if (containsDcd) {
      if (legComponent.leg.productType !== DUAL_CURRENCY_DEPOSIT) {
        form.get('margin')?.setValue('');
        form.get('margin')?.disable();
      }
      ['marginStyle', 'retrocessionFee'].forEach(field => {
        form.get(field)?.setValue('');
        form.get(field)?.disable();
      });
    } else {
      ['margin', 'marginStyle', 'retrocessionFee'].forEach(field => {
        form.get(field)?.enable();
      });
    }
  });

  alterMarginReadOnlyStates(
    !!containsDcd,
    editableSettings,
    legComponents,
    staticRfq
  );
}

function alterMarginReadOnlyStates(
  containsDcd: boolean,
  editableSettings: { editableFields: string[]; lockWhileActive: string[] },
  legComponents: LegComponent[],
  staticRfq: boolean
) {
  [
    'margin',
    'marginAsk',
    'marginBid',
    'retrocessionFee',
    'marginStyle'
  ].forEach(attrField => {
    if (containsDcd) {
      if (staticRfq) {
        const marginIndex = editableSettings.editableFields.findIndex(
          field => field === attrField
        );
        editableSettings.editableFields.splice(marginIndex, 1);
      }
      legComponents.forEach((legComponent, i) => {
        const attribute = getAttributeControlFromKey(
          attrField,
          legComponent.leg.attributesAsFormControls
        );

        if (!attribute) {
          return;
        }
        if (staticRfq) {
          attribute.readOnly = true;
        }
        if (
          legComponent.leg.productType !== DUAL_CURRENCY_DEPOSIT &&
          (attrField === 'margin' ||
            attrField === 'marginAsk' ||
            attrField === 'marginBid')
        ) {
          attribute.display = false;
          attribute.hideAttributeContainer = false;
        } else if (
          attrField !== 'margin' &&
          attrField !== 'marginAsk' &&
          attrField !== 'marginBid'
        ) {
          attribute.hideAttributeContainer = true;
        }
        attribute.validator = [];
      });
    } else {
      const marginControl = editableSettings.editableFields.find(
        field => field === attrField
      );
      if (!marginControl) {
        editableSettings.editableFields.push(attrField);
      }

      legComponents.forEach(legComponent => {
        const attribute = getAttributeControlFromKey(
          attrField,
          legComponent.leg.attributesAsFormControls
        );

        if (!attribute) {
          return;
        }
        const currentDisplay = attribute.display;

        if (
          attrField === 'margin' ||
          attrField === 'marginAsk' ||
          attrField === 'marginBid'
        ) {
          attribute.display = true;
        }

        attribute.readOnly = false;
        attribute.hideAttributeContainer = false;
        if (legComponent.form.get(attrField)) {
          updateValidatorFromMarginStyle(
            legComponent.form.get(attrField)?.value,
            attrField,
            legComponent.leg
          );
        }

        if (!currentDisplay) {
          setTimeout(_ => {
            legComponent.legElemService.allElemListener(attrField);
          }, 0);
        }
      });
    }
  });
}

export function updateMarginActiveAttributes(
  activeAttributes: LegAttribute[],
  containsDcd: boolean,
  useWhiteLabelMargin: boolean
): void {
  if (!useWhiteLabelMargin) {
    return;
  }

  ['retrocessionFee', 'marginStyle'].forEach(attrField => {
    const activeAttribute = activeAttributes.find(
      activeAttribute => activeAttribute.shortName === attrField
    );
    if (activeAttribute) {
      activeAttribute.hide = containsDcd ? true : false;
    }
  });
}

function setStructureDirection(
  rfqTabShortName: string,
  direction: string,
  dtoLeg: OptionLegDTO
): void {
  if (
    rfqTabShortName === 'tpf' ||
    rfqTabShortName === 'simpleTarf' ||
    rfqTabShortName === 'simpleAccumulator'
  ) {
    if (direction === 'Buy') {
      dtoLeg.structureDirection = 'BullishBaseCurrency';
    } else if (direction === 'Sell') {
      dtoLeg.structureDirection = 'BearishBaseCurrency';
    } else {
      throw 'Direction can not be mapped';
    }
  }
}

export function premiumInPercent(
  premiumCurrency: string,
  baseCurrency: string,
  termCurrency: string,
  premiumAmount: number,
  notional: number,
  counterNotional: number
): number {
  if (premiumCurrency === baseCurrency) {
    return premiumAmount / notional;
  } else if (premiumCurrency === termCurrency) {
    return premiumAmount / counterNotional;
  }
  return null;
}

export function midPrice(
  premiumAmountMid: number,
  premiumCurrency: string,
  baseCurrency: string,
  termCurrency: string,
  notional: number,
  counterNotional: number,
  forwardPointsMultiplier: number,
  vols: number[]
): MidPriceInfo {
  const premiumInPercentVal = premiumInPercent(
    premiumCurrency,
    baseCurrency,
    termCurrency,
    premiumAmountMid,
    notional,
    counterNotional
  );
  const pips = (premiumAmountMid / notional) * forwardPointsMultiplier;

  return {
    premiumInPercent: premiumInPercentVal,
    pips,
    premiumAmountMid,
    currency: premiumCurrency,
    vols,
    spotMid: undefined
  };
}

export function getReferenceNotional(
  productType: string,
  schedule: components['schemas']['FixingEventDTO'][],
  baseCurrency: string,
  termCurrency: string,
  hydraLeg: {
    currency: string;
    notional?: string | number;
    notional1?: string | number;
  }
): number {
  if (productType === VANILLA_STRIP) {
    if (hydraLeg.currency === baseCurrency) {
      return schedule.reduce((acc, val) => acc + val.notional, 0);
    } else if (hydraLeg.currency === termCurrency) {
      return schedule.reduce((acc, val) => acc + val.notional / val.strike, 0);
    }
    return null;
  }
  return Utilities.formattedNumberToNumber(
    hydraLeg.notional || hydraLeg.notional1
  );
}

export function getReferenceCounterNotional(
  productType: string,
  schedule: components['schemas']['FixingEventDTO'][],
  baseCurrency: string,
  termCurrency: string,
  hydraLeg: {
    currency: string;
    counterNotional?: string | number;
    counterNotional1?: string | number;
  }
): number {
  if (productType === VANILLA_STRIP) {
    if (hydraLeg.currency === baseCurrency) {
      return schedule.reduce((acc, val) => acc + val.notional * val.strike, 0);
    } else if (hydraLeg.currency === termCurrency) {
      return schedule.reduce((acc, val) => acc + val.notional, 0);
    }
    return null;
  }
  return Utilities.formattedNumberToNumber(
    hydraLeg.counterNotional || hydraLeg.counterNotional1
  );
}
